import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { cx } from '../../lib/format';

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

const ToastContext = createContext<(message: string, tone?: ToastTone) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, tone: ToastTone = 'success') => {
    const id = Date.now() + Math.random();
    setItems((list) => [...list.slice(-2), { id, tone, message }]);
    setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--tabbar-h)+var(--safe-bottom)+24px)] z-[70] flex flex-col items-center gap-2 px-4 lg:bottom-6" aria-live="polite">
        <AnimatePresence initial={false}>
        {items.map((t) => (
          <m.div layout key={t.id} initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }} transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }} role={t.tone === 'error' ? 'alert' : 'status'} className={cx('glass-strong pointer-events-auto flex max-w-[460px] items-center gap-2.5 rounded-[16px] px-4 py-3 text-[13.5px] font-medium text-ink')}>
            {t.tone === 'success' && <CheckCircle2 className="size-[18px] shrink-0 text-success" aria-hidden />}
            {t.tone === 'error' && <AlertCircle className="size-[18px] shrink-0 text-danger" />}
            {t.tone === 'info' && <Info className="size-[18px] shrink-0 text-info" />}
            <span>{t.message}</span>
          </m.div>
        ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
